import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Avatar from './Avatar';

const CATEGORY = {
    TASK_APPROVAL: 'Task approval', TASK: 'Task', LEAVE: 'Leave request', HALF_DAY: 'Half-day / short leave',
    WFH: 'Work from home', EXPENSE: 'Expense', ATTENDANCE: 'Attendance'
};

const formatAt = (value, dateOnly) => {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return date.toLocaleString('en-GB', dateOnly
        ? { day: '2-digit', month: 'short', year: 'numeric' }
        : { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Details for one notification: who is involved, what happened when, and the
 * record's own fields. Action rows carry `people`, `timeline` and `fields` from
 * the server; company-activity rows fall back to recipient and message.
 */
export default function NotificationDetailDrawer({ row, onClose, onApprove, busy, error }) {
    const dialog = useRef(null);
    useEffect(() => {
        const node = dialog.current;
        const trigger = document.activeElement;
        node.showModal();
        return () => { node.close(); trigger?.focus(); };
    }, []);

    const fields = row.fields || [
        { label: 'For', value: row.recipient?.name },
        { label: 'Message', value: row.message },
        { label: 'Created', value: formatAt(row.createdAt) }
    ].filter(f => f.value);
    const now = Date.now();
    const category = CATEGORY[row.category] || row.type;

    return <dialog ref={dialog} className="nc-drawer" aria-labelledby="notification-detail-title"
        onCancel={event => { event.preventDefault(); onClose(); }}
        onClick={event => { if (event.target === dialog.current) onClose(); }}>
        <header className="nc-drawer-head">
            <div className="nc-drawer-heading">
                {(category || row.status) && <div className="nc-drawer-tags">
                    {category && <span className="nc-tag">{category}</span>}
                    {row.status && <span className="nc-tag muted">{row.status}</span>}
                </div>}
                <h2 id="notification-detail-title">{row.title}</h2>
                {row.summary && <p className="nc-drawer-summary">{row.summary}</p>}
            </div>
            <button type="button" className="nc-icon-btn" onClick={onClose} aria-label="Close details">
                <FontAwesomeIcon icon={faXmark} />
            </button>
        </header>

        <div className="nc-drawer-body">
            {row.people?.length > 0 && <section aria-labelledby="nc-people-title">
                <h3 id="nc-people-title">People</h3>
                {row.people.map(group => <div key={group.label} className="nc-people-row">
                    <span className="nc-people-label">{group.label}</span>
                    <ul className="nc-people">
                        {group.users.map(user => <li key={user._id}>
                            <Avatar name={user.name} profilePic={user.profilePic} className="nc-avatar" />
                            <span>{user.name}</span>
                        </li>)}
                    </ul>
                </div>)}
            </section>}

            {row.timeline?.length > 0 && <section aria-labelledby="nc-timeline-title">
                <h3 id="nc-timeline-title">Timeline</h3>
                <ol className="nc-timeline">
                    {row.timeline.map((event, index) => {
                        const upcoming = event.planned && new Date(event.at).getTime() > now;
                        return <li key={index} className={upcoming ? 'is-planned' : ''}>
                            <span className="nc-timeline-dot" aria-hidden="true" />
                            <div>
                                <strong>{event.label}</strong>
                                <span className="nc-timeline-meta">
                                    <time dateTime={new Date(event.at).toISOString()}>{formatAt(event.at, event.dateOnly)}</time>
                                    {event.by && <span> · by {event.by}</span>}
                                    {upcoming && <span> · upcoming</span>}
                                </span>
                                {event.note && <p>{event.note}</p>}
                            </div>
                        </li>;
                    })}
                </ol>
            </section>}

            {fields.length > 0 && <section aria-labelledby="nc-fields-title">
                <h3 id="nc-fields-title">Details</h3>
                <dl className="nc-fields">
                    {fields.map(field => <div key={field.label}><dt>{field.label}</dt><dd>{String(field.value)}</dd></div>)}
                </dl>
            </section>}
        </div>

        {error && <p role="alert" className="nc-error">{error}</p>}
        {(row.approval || row.link) && <footer className="nc-drawer-foot">
            {row.approval && <button type="button" className="nc-btn approve" disabled={busy} onClick={() => onApprove(row)}>
                {busy ? 'Approving…' : 'Approve'}
            </button>}
            {row.link && <Link className="nc-btn ghost" to={row.link} onClick={onClose}>Open full record</Link>}
        </footer>}
    </dialog>;
}
